import { JSONPath } from 'jsonpath-plus'
import { JSONObject, JSONSchemaMetaData } from 'Types/Common.types'

export const getMetadataForPath = (
    metadata: JSONSchemaMetaData | null,
    path: string
): JSONSchemaMetaData | null => {
    if (!metadata || path === '$') {
        return metadata
    }

    // Remove the root $ prefix and split the path
    const pathParts = path
        .replace(/^\$\.?/, '')
        .split(/[.[\]]/)
        .filter(Boolean)

    let current = metadata
    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i]

        if (current && current.type === 'object' && current.children) {
            current = current.children[part] || null
        } else if (current && current.type === 'array' && current.children) {
            // For arrays, we need to get the item metadata
            if (Array.isArray(current.children)) {
                // This is a mixed array with different types
                const index = Number.parseInt(part, 10)
                if (Number.isNaN(index) || index < 0 || index >= current.children.length) {
                    return null
                }
                current = current.children[index] || null
            } else {
                // This is a uniform array
                current = current.children
            }
        } else {
            return null
        }
    }

    return current
}

export const getDisplayName = (
    key: string,
    parentMetadata: JSONSchemaMetaData | null,
    isArray: boolean,
    index: number
): string => {
    if (isArray) {
        return `${index + 1}`
    }

    if (parentMetadata && parentMetadata.type === 'object' && parentMetadata.children) {
        const childMetadata = parentMetadata.children[key]
        if (childMetadata && childMetadata._meta && childMetadata._meta.label) {
            return childMetadata._meta.label
        }
    }

    return key
}

export const getOrder = (
    key: string,
    parentMetadata: JSONSchemaMetaData | null,
    isArray: boolean,
    index: number
): number => {
    if (isArray) {
        return index
    }

    if (parentMetadata && parentMetadata.type === 'object' && parentMetadata.children) {
        const childMetadata = parentMetadata.children[key]
        if (childMetadata && childMetadata._meta && childMetadata._meta.order !== null) {
            return childMetadata._meta.order
        }
    }

    return 999
}

export const jsonPathToDotNotation = (path: string, jsonContent: JSONObject): string => {
    let transformedPath = path
        .replace(/^\$/, '$')
        .replace(/\[['"]([^'"\]]+)['"]\]/g, (_, key) => `.${key}`)

    transformedPath = transformedPath.replace(/\[(\d+)\]/g, (_, index, offset) => {
        const parentPath = transformedPath.substring(0, offset)

        try {
            const parentValue = JSONPath({
                path: parentPath,
                json: jsonContent,
                wrap: false,
            })

            if (
                parentValue !== undefined &&
                typeof parentValue === 'object' &&
                !Array.isArray(parentValue)
            ) {
                return `.${index}`
            }
        } catch (error) {
            return `[${index}]`
        }
        return `[${index}]`
    })

    return transformedPath
}
